<template>
  <div class="mx-auto w-full">
    <div class="mx-[-4.5] flex flex-wrap items-start gap-9 pb-6 lg:pb-9">
      <div
        v-for="(blog, idx) in blogs"
        :key="idx + blog?.slug"
        class="flex-shrink-0 flex-grow basis-1/2 sm:basis-1/3 lg:basis-1/4"
      >
        <PagesBlogCard :blog="blog" />
      </div>
    </div>
    <div
      v-if="!isLoading && blogs.length > 0 && totalPages > 1"
      class="flex justify-center py-6.5"
    >
      <UiPagination
        v-model="filter.page"
        :total-pages="totalPages"
        @update:model-value="changePage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash-es";
import { hash } from "ohash";

const route = useRoute();
const router = useRouter();
const { $api } = useNuxtApp();
const { setBlogListPageLdSchema } = useJsonLdSchema();

interface FilterI {
  page: number;
  count: number;
  search: string;
  categories?: string[];
}

const currentPage = computed(() => {
  return Number(route.query.page) || 1;
});
const filter = computed(() => {
  const params: FilterI = {
    page: currentPage.value,
    count: 9,
    search: (route.query.search as string) || "",
  };

  if (route?.params?.section) {
    params.categories = [route?.params?.section as string];
  }

  return params;
});

const debouncedFilter = refDebounced(filter, 300);

const key = computed(() =>
  hash([(route.params?.section as string) || "all", unref(debouncedFilter)]),
);
const {
  data: blogsResp,
  pending: isLoading,
  error,
} = await useAsyncCachedData(
  key,
  () => {
    return $api.blog.getPosts(objectToQuery(debouncedFilter.value));
  },
  {
    watch: [debouncedFilter],
  },
);

if (error.value) {
  console.error("Error message:", error.value);
}

const blogs = computed(() => blogsResp.value?.data || []);
const totalPages = computed(
  () => (blogsResp.value?.meta?.last_page as number) || 1,
);

const paginationRelLinks = usePaginationRelLinks(
  currentPage.value,
  totalPages.value,
);

useHead({
  link: paginationRelLinks,
});

const scrollToCategoriesAndSearch = debounce(() => {
  const element = document.querySelector("#list");
  if (element) {
    const offset = -60;
    const elementPosition =
      element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: elementPosition + offset,
      behavior: "smooth",
    });
  }
}, 150);

watch(
  () => filter.value.page,
  () => {
    scrollToCategoriesAndSearch();
  },
);

watch(
  () => route.query.search,
  () => {
    changePage(1);
    scrollToCategoriesAndSearch();
  },
);

const blogPageSchemaName = inject(blogPageSchemaNameKey);

setBlogListPageLdSchema({
  name: blogPageSchemaName,
  posts: blogs,
});

function changePage(page) {
  router.replace({ query: { ...route.query, page } });
}
</script>
